import { DEFAULT_COLOR_HEX, normalizeColorHex } from '../../utils/colorHex';

/**
 * The editable brand kit a `brand` element carries: its palette as values, not phrases. The
 * element record owns it; nothing else writes it. The Creative Bible's brand-mode palette is
 * meant to read from here once it views pinned brand elements, so this slice adds no prompt
 * path of its own (`docs/plans/BRAND-KIT-ELEMENT-SPEC.md`).
 */
export const BRAND_COLOR_USAGES = Object.freeze(['primary', 'secondary', 'accent', 'background', 'text']);
export const BRAND_KIT_MAX_COLORS = 8;
const BRAND_COLOR_NAME_MAX = 40;
const DEFAULT_BRAND_COLOR_USAGE = 'accent';

function normalizeBrandColorName(value) {
  return typeof value === 'string' ? value.trim().slice(0, BRAND_COLOR_NAME_MAX).trim() || null : null;
}

// The swatch a new row starts on: white, else the first of these neutrals the palette lacks.
// One per slot, so a palette with room always has one free and "Add color" never writes a
// duplicate the normalizer would drop.
const NEW_ROW_GREY_LEVELS = [0, 128, 192, 64, 224, 32, 160];
const NEW_ROW_HEXES = Object.freeze([
  DEFAULT_COLOR_HEX,
  ...NEW_ROW_GREY_LEVELS.map((level) => normalizeColorHex(level.toString(16).padStart(2, '0').repeat(3), null)),
]);

export function nextBrandColorHex(colors) {
  const used = new Set((Array.isArray(colors) ? colors : []).map((color) => normalizeColorHex(color?.hex, null)));
  return NEW_ROW_HEXES.find((hex) => !used.has(hex)) || DEFAULT_COLOR_HEX;
}

export function normalizeBrandKit(kit) {
  if (!kit || typeof kit !== 'object') return null;
  const colors = [];
  const seenHexes = new Set();
  for (const color of Array.isArray(kit.colors) ? kit.colors : []) {
    const hex = normalizeColorHex(color?.hex, null);
    // A colour is its hex: the first entry for a hex wins and later ones take no slot.
    if (!hex || seenHexes.has(hex)) continue;
    seenHexes.add(hex);
    colors.push({
      hex,
      name: normalizeBrandColorName(color.name),
      usage: BRAND_COLOR_USAGES.includes(color.usage) ? color.usage : DEFAULT_BRAND_COLOR_USAGE,
    });
    if (colors.length === BRAND_KIT_MAX_COLORS) break;
  }
  return colors.length ? { colors } : null;
}
